/* PrizeSlider — liste des lots que le client peut gagner, en slider horizontal.
   Combine le Grand Tirage mensuel (en tête) et les gains instantanés. Design
   sobre et lisible : bordures fines, tons neutres, badge discret et logo du
   sponsor du lot mis en avant en pastille sur le visuel. Slider à défilement
   natif (scroll-snap) + pastilles synchronisées, et défilement automatique tant
   que le passager ne prend pas la main. Ne rend rien sans lot. */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Gift } from "lucide-react";

const AUTOPLAY_MS = 4000;      // temps d'affichage d'un lot
const RESUME_AFTER_MS = 8000;  // reprise après une interaction du passager

// Un lot peut être présenté par une description texte OU uniquement par son visuel.
const hasPrizeInfo = (d) => !!(d?.prizeDescription || d?.prizeImageUrl);

function buildPrizeList(monthlyDraw, instantDraws) {
  const list = [];
  if (hasPrizeInfo(monthlyDraw)) {
    list.push({ ...monthlyDraw, _kind: "MONTHLY" });
  }
  (instantDraws || []).forEach((d) => {
    if (hasPrizeInfo(d)) list.push({ ...d, _kind: "INSTANT" });
  });
  return list;
}

export default function PrizeSlider({ monthlyDraw, instantDraws, texts = {} }) {
  const prizes = useMemo(
    () => buildPrizeList(monthlyDraw, instantDraws),
    [monthlyDraw, instantDraws]
  );
  const scrollerRef = useRef(null);
  const [active, setActive] = useState(0);

  const onScroll = () => {
    const el = scrollerRef.current;
    if (!el) return;
    // La carte active est celle dont le bord gauche est le plus proche du
    // bord gauche du conteneur (compatible scroll-snap).
    const cards = el.children;
    let best = 0;
    let bestDist = Infinity;
    for (let i = 0; i < cards.length; i++) {
      const d = Math.abs(cards[i].offsetLeft - el.scrollLeft);
      if (d < bestDist) {
        bestDist = d;
        best = i;
      }
    }
    if (best !== active) setActive(best);
  };

  const goTo = useCallback((i) => {
    const el = scrollerRef.current;
    if (!el || !el.children[i]) return;
    el.scrollTo({ left: el.children[i].offsetLeft, behavior: "smooth" });
  }, []);

  // Le défilement auto lit l'index courant sans se relancer à chaque carte.
  const activeRef = useRef(0);
  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  // Toute interaction suspend le défilement, qui reprend après un délai.
  const pausedUntilRef = useRef(0);
  const pause = useCallback(() => {
    pausedUntilRef.current = Date.now() + RESUME_AFTER_MS;
  }, []);

  useEffect(() => {
    if (prizes.length < 2) return undefined;
    // Respecte le réglage système « réduire les animations ».
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches) return undefined;

    const id = setInterval(() => {
      // Onglet en arrière-plan : inutile de faire défiler dans le vide.
      if (document.hidden || Date.now() < pausedUntilRef.current) return;
      goTo((activeRef.current + 1) % prizes.length);
    }, AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [prizes.length, goTo]);

  if (!prizes.length) return null;

  return (
    <section data-testid="prize-slider">
      <div className="flex items-baseline justify-between mb-2.5">
        <h2 className="font-heading text-sm font-semibold text-foreground/90">
          {texts.prizesSectionTitle || "Les lots à remporter"}
        </h2>
        <span className="text-[10px] uppercase tracking-[0.18em] font-mono text-muted-foreground">
          {texts.prizesSectionEyebrow || "À gagner"}
        </span>
      </div>

      <div
        ref={scrollerRef}
        onScroll={onScroll}
        onPointerDown={pause}
        onTouchStart={pause}
        onWheel={pause}
        className="flex gap-2.5 overflow-x-auto pb-1 -mx-4 px-4 snap-x snap-mandatory scrollbar-none"
        style={{ scrollbarWidth: "none" }}
      >
        {prizes.map((p, i) => {
          const isMonthly = p._kind === "MONTHLY";
          const badge = isMonthly
            ? texts.prizesMonthlyBadge || "Grand Tirage"
            : texts.prizesInstantBadge || "Gain instantané";
          return (
            <article
              key={p.id || i}
              data-testid={`prize-card-${i}`}
              className="snap-start shrink-0 w-[70%] max-w-[260px] overflow-hidden rounded-xl border border-border bg-card"
            >
              <div className="relative h-28 w-full bg-muted">
                {p.prizeImageUrl ? (
                  <img
                    src={p.prizeImageUrl}
                    alt={p.prizeDescription || p.drawTitle || "Lot à gagner"}
                    className="w-full h-full object-cover"
                    draggable={false}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                    <Gift className="h-8 w-8 opacity-40" />
                  </div>
                )}
                <span className="absolute top-2 left-2 text-[10px] font-medium px-2 py-0.5 rounded-full bg-background/85 text-foreground/70 border border-border">
                  {badge}
                </span>
                {/* Logo du sponsor du lot, en pastille sur le bord du visuel */}
                {p.sponsor?.logoUrl && (
                  <div className="absolute -bottom-4 right-3 h-11 w-11 rounded-full bg-white border border-border grid place-items-center overflow-hidden shadow-sm">
                    <img
                      src={p.sponsor.logoUrl}
                      alt={p.sponsor.name || "Sponsor"}
                      className="h-8 w-8 object-contain"
                      draggable={false}
                    />
                  </div>
                )}
              </div>

              <div className="p-3 pt-3.5">
                <div className="font-medium text-sm leading-snug text-foreground line-clamp-2 pr-10">
                  {p.prizeDescription || p.drawTitle || "Lot surprise"}
                </div>

                <div className="mt-2 flex items-center justify-between gap-2">
                  <span className="text-[11px] text-muted-foreground truncate">
                    {p.sponsor?.name || " "}
                  </span>
                  {p.numberOfPrizes != null && p.numberOfPrizes > 0 && (
                    <span className="text-[10px] font-mono text-muted-foreground shrink-0">
                      {p.numberOfPrizes} lot{p.numberOfPrizes > 1 ? "s" : ""}
                    </span>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {prizes.length > 1 && (
        <div className="mt-2.5 flex items-center justify-center gap-1.5" role="tablist">
          {prizes.map((p, i) => (
            <button
              key={p.id || i}
              aria-label={`Lot ${i + 1}`}
              data-testid={`prize-dot-${i}`}
              onClick={() => {
                pause();
                goTo(i);
              }}
              className="h-1 rounded-full transition-all"
              style={{
                width: i === active ? 16 : 5,
                background:
                  i === active
                    ? "hsl(var(--muted-foreground) / 0.8)"
                    : "hsl(var(--muted-foreground) / 0.3)",
              }}
            />
          ))}
        </div>
      )}
    </section>
  );
}
